<template>
  <div class="device-layout-xuefengshan-global p-4 relative">
    <div class="flex justify-between items-center mb-4 z-10 relative">
      <h3 class="text-lg font-semibold text-primary">广播电视台机房布局监测</h3>
      <div class="text-sm text-gray-300">
        <i class="fa fa-refresh mr-1"></i>最后更新: {{ lastUpdate }}
      </div>
    </div>
    <div ref="sceneContainer" class="scene-container w-full flex-1"></div>
    <div v-if="selectedDevice" class="device-details absolute bottom-4 left-4 bg-dark/80 text-white p-3 rounded-lg border border-primary/30 w-64 z-10">
      <h4 class="font-bold mb-2">{{ selectedDevice.name }}</h4>
      <div class="grid grid-cols-2 gap-2 text-sm">
        <div><span class="text-gray-400">发射功率:</span> {{ selectedDevice.txPower }}W</div>
        <div><span class="text-gray-400">反射功率:</span> {{ selectedDevice.rxPower }}W</div>
        <div><span class="text-gray-400">激励器:</span> {{ selectedDevice.exciter }}%</div>
        <div><span class="text-gray-400">电压:</span> {{ selectedDevice.voltage }}V</div>
        <div><span class="text-gray-400">电流:</span> {{ selectedDevice.current }}A</div>
        <div><span class="text-gray-400">状态:</span> <span :class="selectedDevice.status === 'normal' ? 'text-green-400' : 'text-red-400'">{{ selectedDevice.status }}</span></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import TWEEN from '@tweenjs/tween.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
// import api from '../utils/api';

// 设备数据模拟函数
const generateDeviceData = () => ({
  txPower: (500 + Math.random() * 500).toFixed(1),
  rxPower: (5 + Math.random() * 20).toFixed(1),
  exciter: (95 + Math.random() * 5).toFixed(1),
  voltage: (220 + Math.random() * 10).toFixed(1),
  current: (5 + Math.random() * 15).toFixed(1),
  status: Math.random() > 0.1 ? 'normal' : 'abnormal'
});

// 机房设备布局配置
const DEVICE_LAYOUT = [
  { id: 1, name: '发射机A', x: -400, y: 0, z: -300, rotation: 0 },
  { id: 2, name: '发射机B', x: 0, y: 0, z: -300, rotation: 0 },
  { id: 3, name: '发射机C', x: 400, y: 0, z: -300, rotation: 0 },
  { id: 4, name: '激励器组A', x: -400, y: 0, z: 0, rotation: Math.PI / 2 },
  { id: 5, name: '激励器组B', x: 400, y: 0, z: 0, rotation: Math.PI / 2 },
  { id: 6, name: '电源系统A', x: -400, y: 0, z: 300, rotation: 0 },
  { id: 7, name: '电源系统B', x: 0, y: 0, z: 300, rotation: 0 },
  { id: 8, name: '电源系统C', x: 400, y: 0, z: 300, rotation: 0 },
];

export default {
  name: "DeviceLayoutXuefengshanGlobal",
  data() {
    return {
      lastUpdate: '',
      selectedDevice: null,
      scene: null,
      camera: null,
      renderer: null,
      labelRenderer: null,
      controls: null,
      devices: [],
      animationId: null,
      currentViewIndex: 0,
      views: [
        { position: { x: 800, y: 500, z: 800 }, target: { x: 0, y: 0, z: 0 } },
        { position: { x: -800, y: 500, z: 800 }, target: { x: 0, y: 0, z: 0 } },
        { position: { x: 0, y: 800, z: 0 }, target: { x: 0, y: 0, z: 0 } },
        { position: { x: 0, y: 200, z: 800 }, target: { x: 0, y: 0, z: -200 } },
      ]
    };
  },

  mounted() {
    this.initScene();
    this.createDevices();
    this.startAnimation();
    this.updateDeviceData();
    setInterval(() => this.updateDeviceData(), 5000);
    setInterval(() => this.switchView(), 10000);
  },

  beforeUnmount() {
    cancelAnimationFrame(this.animationId);
    this.renderer.dispose();
    this.labelRenderer.dispose();
    this.scene.clear();
  },

  methods: {
    initScene() {
      // 创建场景
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x0a0e17);

      // 创建网格辅助线
      const gridHelper = new THREE.GridHelper(1000, 20, 0x1a2130, 0x1a2130);
      this.scene.add(gridHelper);

      // 创建坐标轴
      const axesHelper = new THREE.AxesHelper(500);
      this.scene.add(axesHelper);

      // 创建相机
      this.camera = new THREE.PerspectiveCamera(75, this.$refs.sceneContainer.clientWidth / this.$refs.sceneContainer.clientHeight, 1, 2000);
      this.camera.position.set(800, 500, 800);

      // 创建光源
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
      this.scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(500, 1000, 500);
      this.scene.add(directionalLight);

      // 创建渲染器
      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize(this.$refs.sceneContainer.clientWidth, this.$refs.sceneContainer.clientHeight);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.$refs.sceneContainer.appendChild(this.renderer.domElement);

      // 创建CSS2D渲染器用于标签
      this.labelRenderer = new CSS2DRenderer();
      this.labelRenderer.setSize(this.$refs.sceneContainer.clientWidth, this.$refs.sceneContainer.clientHeight);
      this.labelRenderer.domElement.style.position = 'absolute';
      this.labelRenderer.domElement.style.top = '0';
      this.labelRenderer.domElement.style.pointerEvents = 'none';
      this.$refs.sceneContainer.appendChild(this.labelRenderer.domElement);

      // 控制器
      this.controls = new OrbitControls(this.camera, this.labelRenderer.domElement);
      this.controls.target.set(0, 0, 0);
      this.controls.enableDamping = true;

      // 窗口大小调整
      window.addEventListener('resize', this.onWindowResize);

      // 动画循环
      const animate = () => {
        this.animationId = requestAnimationFrame(animate);
        TWEEN.update();
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
        this.labelRenderer.render(this.scene, this.camera);
      };

      animate();
    },

    createDevices() {
      // 创建设备模型和标签
      DEVICE_LAYOUT.forEach(device => {
        // 创建设备几何体
        const geometry = new THREE.BoxGeometry(200, 150, 100);
        const material = new THREE.MeshStandardMaterial({
          color: 0x1e40af,
          transparent: true,
          opacity: 0.8,
          side: THREE.DoubleSide
        });
        const mesh = new THREE.Mesh(geometry, material);

        // 设置设备位置和旋转
        mesh.position.set(device.x, device.y, device.z);
        mesh.rotation.y = device.rotation;
        mesh.userData = { type: 'device', id: device.id };

        // 添加设备边框
        const edges = new THREE.EdgesGeometry(geometry);
        const lineMaterial = new THREE.LineBasicMaterial({ color: 0x3b82f6 });
        const wireframe = new THREE.LineSegments(edges, lineMaterial);
        mesh.add(wireframe);

        // 创建设备标签
        const labelDiv = document.createElement('div');
        labelDiv.className = 'device-label bg-primary/80 text-white px-2 py-1 rounded text-xs';
        labelDiv.textContent = device.name;
        labelDiv.style.userSelect = 'none';

        const labelObject = new CSS2DObject(labelDiv);
        labelObject.position.set(0, 100, 0);
        mesh.add(labelObject);

        // 添加到场景
        this.scene.add(mesh);

        // 存储设备引用
        this.devices.push({
          id: device.id,
          name: device.name,
          mesh,
          ...generateDeviceData()
        });
      });

      // 添加点击事件监听
      this.addRaycasterListener();
    },

    addRaycasterListener() {
      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2();

      const onClick = (event) => {
        // 计算鼠标位置
        mouse.x = (event.clientX - this.$refs.sceneContainer.getBoundingClientRect().left) / this.$refs.sceneContainer.clientWidth * 2 - 1;
        mouse.y = -(event.clientY - this.$refs.sceneContainer.getBoundingClientRect().top) / this.$refs.sceneContainer.clientHeight * 2 + 1;

        // 更新射线
        raycaster.setFromCamera(mouse, this.camera);

        // 检测相交对象
        const intersects = raycaster.intersectObjects(this.scene.children, true);

        if (intersects.length > 0) {
          const selectedObject = intersects[0].object;
          if (selectedObject.userData.type === 'device') {
            const device = this.devices.find(d => d.id === selectedObject.userData.id);
            if (device) {
              this.selectedDevice = { ...device };
              this.highlightDevice(device.mesh);
            }
          }
        } else {
          this.selectedDevice = null;
          this.devices.forEach(d => this.resetDeviceHighlight(d.mesh));
        }
      };

      this.$refs.sceneContainer.addEventListener('click', onClick);
    },

    highlightDevice(mesh) {
      // 重置所有设备高亮
      this.devices.forEach(d => this.resetDeviceHighlight(d.mesh));

      // 设置选中设备高亮
      mesh.material.color.set(0x3b82f6);
      mesh.material.opacity = 1;

      // 添加脉冲动画
      const animateScale = () => {
        new TWEEN.Tween(mesh.scale)
          .to({ x: 1.05, y: 1.05, z: 1.05 }, 500)
          .easing(TWEEN.Easing.Sinusoidal.InOut)
          .yoyo(true)
          .repeat(Infinity)
          .start();
      };

      animateScale();
    },

    resetDeviceHighlight(mesh) {
      mesh.material.color.set(0x1e40af);
      mesh.material.opacity = 0.8;
      TWEEN.removeAll();
      mesh.scale.set(1, 1, 1);
    },

    updateDeviceData() {
      // 更新最后更新时间
      this.lastUpdate = new Date().toLocaleTimeString();

      // 更新设备数据
      this.devices.forEach(device => {
        const newData = generateDeviceData();
        // 使用TWEEN实现数据平滑过渡动画
        Object.keys(newData).forEach(key => {
          if (typeof device[key] === 'string') device[key] = parseFloat(device[key]);
          new TWEEN.Tween(device)
            .to({ [key]: parseFloat(newData[key]) }, 1000)
            .easing(TWEEN.Easing.Quadratic.InOut)
            .start();
        });
      });

      // 如果有选中设备，更新其数据
      if (this.selectedDevice) {
        const selected = this.devices.find(d => d.id === this.selectedDevice.id);
        if (selected) {
          this.selectedDevice = { ...this.selectedDevice, ...selected };
        }
      }
    },

    switchView() {
      // 切换视角
      this.currentViewIndex = (this.currentViewIndex + 1) % this.views.length;
      const targetView = this.views[this.currentViewIndex];

      // 使用TWEEN实现相机平滑过渡
      new TWEEN.Tween(this.camera.position)
        .to(targetView.position, 3000)
        .easing(TWEEN.Easing.Quadratic.InOut)
        .start();

      new TWEEN.Tween(this.controls.target)
        .to(targetView.target, 3000)
        .easing(TWEEN.Easing.Quadratic.InOut)
        .onUpdate(() => {
          this.controls.target.copy(this.controls.target);
        })
        .start();
    },

    onWindowResize() {
      // 窗口大小调整处理
      this.camera.aspect = this.$refs.sceneContainer.clientWidth / this.$refs.sceneContainer.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(this.$refs.sceneContainer.clientWidth, this.$refs.sceneContainer.clientHeight);
      this.labelRenderer.setSize(this.$refs.sceneContainer.clientWidth, this.$refs.sceneContainer.clientHeight);
    }
  }
};
</script>

<style scoped>
.device-layout-xuefengshan-global {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.scene-container {
  width: 100%;
  height: 0;
  flex-grow: 1;
  position: relative;
  overflow: hidden;
}

.text-gray-300 {
  color: #dcdcdc;
}

/* 设备详情面板样式 */
.device-details {
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  border-left: 3px solid #3b82f6;
}

/* 设备标签样式 */
::v-deep .device-label {
  white-space: nowrap;
  font-weight: bold;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .device-details {
    width: calc(100% - 2rem);
  }
}
</style>
